import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useStartMutation } from '../../__generated__/chirp/com/hubspot/dpg/devex/agents/rpc/WorkspacesRpc.hooks';
import { navigateToWorkspace } from '../utils/urls';

export interface SharedPromptInputProps {
  disabled?: boolean;
  initialPrompt?: string;
  onChange?: (value: string) => void;
  onError?: (error?: unknown) => void;
  onSubmit?: (prompt: string) => void;
  onWorkspaceCreated?: (workspaceId: number, prompt: string) => void;
  referrer?: string;
  value?: string;
}

export function useAppBuilderPromptSubmission({
  disabled,
  initialPrompt,
  onChange,
  onError,
  onSubmit,
  onWorkspaceCreated,
  referrer,
  value,
}: SharedPromptInputProps) {
  const [internalPrompt, setInternalPrompt] = useState(initialPrompt ?? '');
  const [startWorkspace, { loading: isSubmitting }] = useStartMutation();

  const promptValue = value ?? internalPrompt;
  const isSubmitDisabled = isSubmitting || !!disabled || !promptValue.trim();

  const handleSubmit = () => {
    if (isSubmitDisabled) return;

    onSubmit?.(promptValue);

    startWorkspace({ variables: { initialPrompt: promptValue } })
      .then((result) => {
        const workspaceId = result.data?.result.id;

        if (workspaceId != null) {
          onWorkspaceCreated?.(workspaceId, promptValue);
          navigateToWorkspace(workspaceId, {
            pending: 'true',
            ...(referrer ? { referrer } : {}),
          });
        } else {
          onError?.();
        }
      })
      .catch((e) => onError?.(e));
  };

  const handleChange = (next: string) => {
    setInternalPrompt(next);
    onChange?.(next);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return {
    promptValue,
    isSubmitting,
    isSubmitDisabled,
    handleChange,
    handleKeyDown,
    handleSubmit,
  };
}
